<template>
  <h5 class="mb-3">{{ name }}</h5>

  <div class="mb-3">
    <currency-input
      ref="currentValue"
      description="current value"
      elementId="currentValue"
      @entered="onSaveShortcut"
    />
  </div>

  <div class="mb-3">
    <yes-no
      questionKey="stillPaying"
      question="Are you still paying into the savings pots?"
      :answer="isStillPaying"
      @answerChanged="onIsStillPayingAnswerChanged"
    />
  </div>

  <div class="mb-3">
    <guiide-transition
      :sectionVisible="showHiddenSection"
      :slideIn="showContribDetail"
    >
      <div>
        <div class="alert alert-success">
          <i class="bi bi-patch-check-fill"></i>
          Ok we will add any payments to the expected value at your target
          retirement date.
        </div>

        <div class="mb-3">
          <currency-input
            ref="monthlyPayment"
            description="monthly payment"
            elementId="monthlyPayment"
            @entered="onSaveShortcut"
          />
        </div>

        <div class="mb-3">
          <yes-no
            questionKey="willIncrease"
            question="Will these increase if your income rises?"
            :answer="willPaymentsIncrease"
            @answerChanged="onWillPaymentsIncreaseAnswerChanged"
          />
        </div>
      </div>
    </guiide-transition>
  </div>

  <bad-response-viewer ref="badResponseViewer" />

  <div class="d-grid gap-2 d-md-flex justify-content-md-end mt-4">
    <button
      type="button"
      class="btn btn-link text-dark text-decoration-none me-md-2"
      aria-label="Close"
      v-on:click="onCancelClicked"
    >
      Cancel
    </button>

    <lock-button :text="buttonText" :async-action="onSaveClicked"></lock-button>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, provide } from "vue";
import { Saving } from "@/guiide/ts/saving";
import ObjectBuilder from "@/guiide/ts/objectBuilder";
import Constants from "@/guiide/ts/constants";
import CurrencyInput from "../../../shared/components/CurrencyInput.vue";
import GuiideTransition from "../../../shared/components/GuiideTransition.vue";
import LockButton from "@/guiide/components/common/LockButton.vue";
import BadResponseViewer from "../../../shared/components/BadResponseViewer.vue";
import YesNo from "../common/YesNo.vue";
import Convert from "../../../shared/ts/convert";
import { app } from "../../ts/app";

export default defineComponent({
  name: "SavingEditor",
  emits: ["edit-finished"],
  components: {
    CurrencyInput,
    GuiideTransition,
    YesNo,
    LockButton,
    BadResponseViewer
  },
  setup() {
    const currentValue = ref(null);
    const flagInvalidAnswer = ref(false);
    const monthlyPayment = ref(null);
    const badResponseViewer = ref(null);
    const execute = ref(false);

    provide('execute', execute);

    return {
      currentValue,
      flagInvalidAnswer,
      monthlyPayment,
      badResponseViewer,
      execute
    };
  },
  data() {
    return {
      saving: null as Saving,
      hiddenSectionHasBeenSeen: false,
      name: "",
      isStillPaying: true,
      showContribDetail: false,
      willPaymentsIncrease: false
    };
  },
  computed: {
    showValidation: function() {
      return this.flagInvalidAnswer;
    },
    buttonText: function(): string {
      return (this.saving?.id ?? Constants.NewId) === Constants.NewId
        ? "Add"
        : "Update";
    },
    showHiddenSection: function(): boolean {
      return this.isStillPaying || this.hiddenSectionHasBeenSeen;
    }
  },
  methods: {
    onStillPayingChange() {
      this.showContribDetail = !this.showContribDetail;
    },
    onIsStillPayingAnswerChanged(answer: string) {
      this.isStillPaying = Convert.toBoolean(answer);
      this.showContribDetail = this.isStillPaying;
      this.hiddenSectionHasBeenSeen = true;
    },
    onWillPaymentsIncreaseAnswerChanged(answer: string) {
      this.willPaymentsIncrease = Convert.toBoolean(answer);
    },
    setValues(saving: Saving) {
      this.badResponseViewer.clear();
      this.saving = saving;
      this.name = saving.name;
      this.currentValue.setValue(saving.currentValue);
      this.isStillPaying = saving.isStillPaying ?? false;
      this.hiddenSectionHasBeenSeen = saving.isStillPaying;
      this.showContribDetail = this.isStillPaying;
      this.monthlyPayment.setValue(saving.monthlyPayment);
      this.willPaymentsIncrease = saving.willPaymentsIncrease;
    },
    onCancelClicked: function() {
      this.editIsFinished();
    },
    onSaveShortcut: async function() {
      this.execute = true;
    },
    onSaveClicked: async function() {
      if (!this.currentValue.validate()) {
        this.execute = false;
        return;
      }

      if (this.isStillPaying) {
        if (!this.monthlyPayment.validate()) {
          this.execute = false;
          return;
        }
      }

      const update = ObjectBuilder.createSaving(
        this.saving.id,
        this.saving.name,
        this.currentValue.getValue(),
        this.isStillPaying,
        this.monthlyPayment.getValue() ?? 0,
        this.willPaymentsIncrease
      );

      const responseTracker = await app.updateSaving(update);
      this.execute = false;

      if (responseTracker.isOk) {
        this.editIsFinished();
      } else {
        if (responseTracker.trackerWillHandleFailure) {
          responseTracker.handleFailure();
        } else {
          this.badResponseViewer.update(
            responseTracker.isOk,
            responseTracker.message
          );
        }
      }
    },
    editIsFinished() {
      this.currentValue.hideAnyWarnings();
      this.monthlyPayment.hideAnyWarnings();
      this.$emit("edit-finished");
    },
    getYearsPayableFrom() {
      const currentYear = new Date().getFullYear();
      const allYears = [];
      const lastYear = currentYear + 10;

      for (let i = currentYear; i <= lastYear; i++) {
        allYears.push(i);
      }

      return allYears;
    }
  }
});
</script>

<style>

</style>
